import { Panel, PickerInput, RadioGroup, TextArea } from '@epam/promo';
import { useArrayDataSource } from '@epam/uui-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  AISuggestionWrapper,
  AISuggestionWrapperChildrenType,
  TABS,
} from 'components/common/AISuggestionWrapper';
import { useAppLocation } from 'hooks/helpers';
import { recognitionRoutes } from 'router/recognitionRoutes';
import { REWARDS, useRecognitionStore } from 'store/recognition';
import { FileLoader, NumericInputCustom, RewardLink } from './components';
import css from './FormBody.module.scss';
import { InlineLabelInput } from 'components/common';
import { AllStatuses } from 'interfaces';
import { RECOGNITION_MODEL } from 'models/recognition.model';
import { getTasks } from 'api/task.service';

export const FormBody = () => {
  const location = useLocation();
  const { isCreate } = useAppLocation(recognitionRoutes);
  const {
    errors,
    draft: draftRecognition,
    recognitions,
    actions: {
      setDraftTitle,
      setDraftDescription,
      setDraftAmountCompletedTasks,
      setDraftReward,
      setDraftStickerpack,
      setDraftFeedback,
      setDraftPictures,
      setDraftType,
      setDraftTaskIds,
    },
  } = useRecognitionStore((state) => state);
  const {
    id,
    title,
    description,
    completedTasks,
    reward,
    stickerPackLink,
    feedbackFormLink,
    status,
  } = draftRecognition;
  const [isRecognitionNew, setIsRecognitionNew] = useState(false);

  const inputElement = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const titleTextfield = inputElement.current?.childNodes[0] as HTMLElement;
    const isAllRecognitionStatusUpdated = recognitions.every(
      (recognitionItem) => recognitionItem.status !== AllStatuses.NOT_SAVED,
    );

    if (
      !draftRecognition.id &&
      isAllRecognitionStatusUpdated &&
      !isRecognitionNew
    ) {
      titleTextfield.focus();
      setIsRecognitionNew(true);
    } else if (isRecognitionNew && draftRecognition.id) {
      setIsRecognitionNew(false);
    }
  }, [draftRecognition, isRecognitionNew, recognitions, location.pathname]);

  const dataSource = useArrayDataSource({ items: REWARDS }, []);
  const rewardValue = REWARDS.find((r) => r.value === reward);

  const [tasks, setTasks] = useState<{ id: string; title: string }[]>([]);

  useEffect(() => {
    getTasks({}).then((data) => {
      const sorted = data.map(({ id, title }) => ({ id, title }));

      setTasks(sorted);
    });
  }, []);

  const dataTasksSource = useArrayDataSource(
    {
      items: tasks,
      getId(item) {
        return item.id;
      },
    },
    [],
  );
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);

  const isReadonly = useMemo(
    () => status === 'PENDING_APPROVAL' || status === 'APPROVED',
    [status],
  );

  const readOnlyTasks = useMemo(() => {
    return tasks
      .filter((task) => draftRecognition.taskIds.some((id) => id === task.id))
      .map((task) => task.title)
      .join(', ');
  }, [draftRecognition.taskIds, tasks]);

  const getDescription: AISuggestionWrapperChildrenType = ({
    onBlur,
    onFocus,
    isAISuggestionBlockShown,
  }) => (
    <div key={id}>
      <TextArea
        autoSize
        inputCx={{
          [css.textarea__input]: true,
          [css['textarea__input--error']]: errors.description,
          [css['textarea__input--disabled']]: isAISuggestionBlockShown,
        }}
        isDisabled={isReadonly || isAISuggestionBlockShown}
        isInvalid={Boolean(errors.description)}
        placeholder="Add description"
        rawProps={{ 'data-testid': 'description-input' }}
        size="48"
        value={description}
        onBlur={(event) => {
          setDraftDescription(event.target.value.trim(), {
            markAsDirty: false,
            shouldValidate: true,
          });

          onBlur(event);
        }}
        onFocus={onFocus}
        onValueChange={setDraftDescription}
      />
    </div>
  );

  return (
    <Panel
      cx={`${isReadonly ? css.form__disabled : ''} ${css.form} ${
        css.recognitionForm
      }`}>
      <InlineLabelInput
        isRequired
        isTitle
        errorText={errors.title}
        isReadonly={isReadonly}
        label="Title"
        readonlyValue={title}>
        <TextArea
          ref={inputElement}
          autoSize
          isRequired
          inputCx={{
            [css.textarea__input]: true,
            [css.textarea__title]: !!title,
            [css['textarea__input--error']]: errors.title,
          }}
          isDisabled={isReadonly}
          isInvalid={Boolean(errors.title)}
          placeholder="Add title"
          rawProps={{ 'data-testid': 'title-input' }}
          size="48"
          value={title}
          onBlur={({ target: { value } }) =>
            setDraftTitle(value.trim(), {
              markAsDirty: false,
              shouldValidate: true,
            })
          }
          onValueChange={setDraftTitle}
        />
      </InlineLabelInput>

      <InlineLabelInput
        isRequired
        errorText={errors.description}
        isReadonly={isReadonly}
        label="Description"
        readonlyValue={description}>
        <AISuggestionWrapper
          options={{
            tab: TABS.RECOGNITIONS,
            field: 'description',
          }}
          setValue={setDraftDescription}
          value={description}>
          {getDescription}
        </AISuggestionWrapper>
      </InlineLabelInput>

      <InlineLabelInput
        isRequired
        errorText={errors.type}
        isReadonly={isReadonly}
        label="Type"
        readonlyValue={RECOGNITION_MODEL[draftRecognition.type]}>
        <RadioGroup
          direction="vertical"
          items={Object.entries(RECOGNITION_MODEL).map(([key, value]) => ({
            id: key,
            name: value,
          }))}
          value={draftRecognition.type}
          onValueChange={setDraftType}
        />
      </InlineLabelInput>

      {draftRecognition.type === 'BY_TASK' && (
        <InlineLabelInput
          isRequired
          errorText={errors.completedTasks}
          isReadonly={isReadonly}
          label="Tasks"
          readonlyValue={readOnlyTasks}>
          <PickerInput
            dataSource={dataTasksSource}
            emptyValue={[]}
            getName={(item) => item!.title}
            isInvalid={!!errors.completedTasks}
            placeholder={tasks.length === 0 ? 'Loading...' : 'Add tasks'}
            selectionMode="multi"
            value={tasks.length > 0 ? draftRecognition.taskIds : []}
            valueType="id"
            onValueChange={setDraftTaskIds}
          />
        </InlineLabelInput>
      )}

      {draftRecognition.type === 'BY_COUNT' && (
        <InlineLabelInput
          isRequired
          errorText={errors.completedTasks}
          isReadonly={isReadonly}
          label="Completed"
          readonlyValue={`${completedTasks} completed tasks`}>
          <NumericInputCustom
            handleChange={setDraftAmountCompletedTasks}
            hasError={!!errors.completedTasks}
            value={Number(completedTasks)}
            onBlur={(value) =>
              setDraftAmountCompletedTasks(value, {
                markAsDirty: false,
                shouldValidate: true,
              })
            }
          />
        </InlineLabelInput>
      )}

      <InlineLabelInput
        isReadonly={isReadonly}
        label="Reward"
        readonlyValue={rewardValue?.label ?? ''}>
        <PickerInput
          disableClear
          dataSource={dataSource}
          dropdownPlacement="bottom-end"
          emptyValue={null}
          getName={(item) => item?.label ?? ''}
          inputCx={{
            [css.textarea__input]: true,
            [css.form__picker]: true,
            [css[`width-${rewardValue?.label}`]]: true,
          }}
          isDisabled={isReadonly}
          placeholder="Add reward"
          rawProps={{ input: { 'data-testid': 'reward-picker-input' } }}
          searchPosition="none"
          selectionMode="single"
          size="48"
          value={rewardValue?.id || null}
          valueType="id"
          onValueChange={setDraftReward}
        />
      </InlineLabelInput>

      {rewardValue?.value === 'STICKER_PACK' && (
        <RewardLink
          autoFocus={isCreate}
          errorMessage={errors.stickerPackLink}
          isReadonly={isReadonly}
          rewardValue={rewardValue}
          value={stickerPackLink}
          onChange={setDraftStickerpack}
        />
      )}

      {rewardValue?.value === 'FEEDBACK_FORM' && (
        <RewardLink
          autoFocus={isCreate}
          errorMessage={errors.feedbackFormLink}
          isReadonly={isReadonly}
          rewardValue={rewardValue}
          value={feedbackFormLink}
          onChange={setDraftFeedback}
        />
      )}

      {rewardValue?.value === 'PICTURE' && (
        <InlineLabelInput align="top" label="Your files">
          <FileLoader
            attachments={draftRecognition.attachments}
            isReadonly={isReadonly}
            setDraftPictures={setDraftPictures}
          />
        </InlineLabelInput>
      )}
    </Panel>
  );
};
